import { useCallback, useRef, useState } from 'react'

/* ============================================================
   useEditHistory — PRD §8.1 CMS
   Simple undo/redo stack for content map snapshots.
   ============================================================ */

type ContentMap = Record<string, Record<string, string>>

interface HistoryState {
  past: ContentMap[]
  present: ContentMap
  future: ContentMap[]
}

const MAX_HISTORY = 50

export function useEditHistory(initialState: ContentMap = {}) {
  const [history, setHistory] = useState<HistoryState>({
    past: [],
    present: initialState,
    future: [],
  })

  // Track whether we've been initialized with real data
  const initialized = useRef(false)

  /** Initialize or reset with fresh data from DB */
  const reset = useCallback((state: ContentMap) => {
    initialized.current = true
    setHistory({ past: [], present: state, future: [] })
  }, [])

  /** Push a new state (user made an edit) */
  const push = useCallback((updater: (prev: ContentMap) => ContentMap) => {
    setHistory((h) => {
      const next = updater(h.present)
      return {
        past: [...h.past.slice(-MAX_HISTORY + 1), h.present],
        present: next,
        future: [], // Clear redo stack on new edit
      }
    })
  }, [])

  /** Update a single field */
  const updateField = useCallback(
    (section: string, key: string, value: string) => {
      push((prev) => ({
        ...prev,
        [section]: { ...(prev[section] || {}), [key]: value },
      }))
    },
    [push],
  )

  /** Undo last change */
  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.past.length === 0) return h
      const previous = h.past[h.past.length - 1]
      return {
        past: h.past.slice(0, -1),
        present: previous,
        future: [h.present, ...h.future],
      }
    })
  }, [])

  /** Redo last undone change */
  const redo = useCallback(() => {
    setHistory((h) => {
      if (h.future.length === 0) return h
      const next = h.future[0]
      return {
        past: [...h.past, h.present],
        present: next,
        future: h.future.slice(1),
      }
    })
  }, [])

  return {
    state: history.present,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    push,
    updateField,
    undo,
    redo,
    reset,
  }
}
